/**
 * 页面追踪器
 * 专门用于页面访问事件的手动上报
 */

import { stats } from './stats';
import { getPlatform, inWebView } from './platform';

/**
 * stats.event 参数接口
 */
interface StatsEventParams {
  event: string;
  type: number;
  record: string;
  ext: string;
  platform?: string;
  ua?: string;
}

/**
 * 页面浏览时长缓存数据接口
 * 直接存储 stats.event 的参数对象，避免数据转换
 */
interface PageDurationCacheData {
  statsParams: StatsEventParams;
  timestamp: number;
  title?: string;
  platform?: string;
  ua?: string;
}

/**
 * 页面浏览时长缓存管理器
 * 遵循单一职责原则，专门负责浏览时长数据的本地缓存管理
 */
class PageDurationCacheManager {
  private static readonly CACHE_KEY = 'page_duration_cache';
  private static readonly MAX_CACHE_SIZE = 100; // 最大缓存条数

  /**
   * 添加页面浏览时长数据到缓存
   */
  public static addToCache(data: PageDurationCacheData): void {
    try {
      const cacheList = this.getCacheList();
      cacheList.push(data);

      // 限制缓存大小，避免localStorage溢出
      if (cacheList.length > this.MAX_CACHE_SIZE) {
        const removedCount = cacheList.length - this.MAX_CACHE_SIZE;
        cacheList.splice(0, removedCount);
      }

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheList));
    } catch (error) {
      // 静默处理缓存写入失败
    }
  }

  /**
   * 获取缓存中的所有数据
   */
  public static getCacheList(): PageDurationCacheData[] {
    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      return cacheData ? JSON.parse(cacheData) : [];
    } catch (error) {
      // 缓存数据损坏时返回空数组
      return [];
    }
  }

  /**
   * 清空缓存
   */
  public static clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      // 静默处理缓存清理失败
    }
  }

  /**
   * 获取缓存数据数量
   */
  public static getCacheSize(): number {
    return this.getCacheList().length;
  }
}

/**
 * 页面追踪器类
 * 遵循单一职责原则，专门负责页面访问事件的上报
 * 需要在各个页面手动调用trackCurrentPage方法
 */
export class PageTracker {
  private static isInitialized = false;
  private static currentPath = '';
  private static startTime = Date.now();
  private static hasCachedCurrentSession = false; // 新增：标记当前会话是否已缓存

  /**
   * 初始化页面追踪器
   * 只需要在应用启动时调用一次，不会自动监听路由变化
   */
  public static async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // 确保Stats已经初始化
      await stats.init();
      
      this.isInitialized = true;
      
      // 应用启动时批量上报缓存的页面浏览时长数据
      await this.batchReportCachedDurations();
      
    } catch (error) {
      // 静默处理初始化失败
    }
  }

  /**
   * 批量上报缓存的页面浏览时长数据
   * 只有在WebView环境中才会上报
   */
  public static async batchReportCachedDurations(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      const cachedData = PageDurationCacheManager.getCacheList();
      
      if (cachedData.length === 0) {
        return;
      }

      // 批量上报所有缓存数据
      const reportPromises = cachedData.map((data) => 
        stats.event(data.statsParams).catch(() => {
          // 单个上报失败不影响其他数据
        })
      );

      await Promise.allSettled(reportPromises);
      
      // 上报成功后清空缓存
      PageDurationCacheManager.clearCache();
      
    } catch (error) {
      // 静默处理批量上报失败
    }
  }

  /**
   * 上报当前页面访问事件
   * 需要在每个页面手动调用此方法
   */
  public static async trackCurrentPage(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      const currentPath = this.getCurrentPath();
      const currentDomain = this.getCurrentDomain();

      // 避免重复上报同一页面
      if (currentPath === this.currentPath) {
        return;
      }

      await stats.event({
        event: 'ip_url_record',  // 固定值
        type: 2,                 // 固定值
        record: currentPath,     // 页面路径
        ext: currentDomain,      // 页面域名
        platform: getPlatform(),
        ua: navigator.userAgent,
      });

      this.currentPath = currentPath;
      this.startTime = Date.now();
      this.hasCachedCurrentSession = false; // 重置缓存标志

      // 页面访问上报完成
    } catch (error) {
      // 静默处理页面访问上报失败
    }
  }

  /**
   * 手动上报指定页面（用于特殊场景）
   */
  public static async trackPage(pagePath: string, domain?: string): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      await stats.event({
        event: 'ip_url_record',
        type: 2,
        record: pagePath,
        ext: domain || this.getCurrentDomain()
      });

      // 手动页面访问上报完成
    } catch (error) {
      // 静默处理手动页面访问上报失败
    }
  }

  /**
   * 缓存页面停留时间（页面离开时调用）
   * 改为写入localStorage缓存，而不是立即上报
   * 只有在WebView环境中才会缓存
   */
  public static cachePageDuration(): void {
    if (!this.isInitialized) {
      return;
    }

    // 防止同一会话重复缓存
    if (this.hasCachedCurrentSession) {
      return;
    }

    try {
      const duration = Date.now() - this.startTime;
      const durationData: PageDurationCacheData = {
        statsParams: {
          event: 'gogolily_app_page_duration',
          type: 2,
          record: window.location.href,
          ext: `${Math.round(duration / 1000)}s`,
          platform: getPlatform(),
          ua: navigator.userAgent,
        },
        timestamp: Date.now(),
        title: document.title
      };

      // 写入localStorage缓存
      PageDurationCacheManager.addToCache(durationData);
      
      // 标记当前会话已缓存
      this.hasCachedCurrentSession = true;

    } catch (error) {
      // 静默处理页面停留时间缓存失败
    }
  }

  /**
   * 立即上报页面停留时间（兼容旧接口）
   * 只有在WebView环境中才会上报
   * @deprecated 建议使用 cachePageDuration 方法
   */
  public static async trackPageDuration(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      const duration = Date.now() - this.startTime;
      
      await stats.event({
        event: 'gogolily_app_page_duration',
        type: 2,
        record: window.location.href,
        ext: `${Math.round(duration / 1000)}s`,
        platform: getPlatform(),
        ua: navigator.userAgent,
      });

      // 页面停留时间上报完成
    } catch (error) {
      // 静默处理页面停留时间上报失败
    }
  }

  /**
   * 获取当前页面路径
   */
  public static getCurrentPath(): string {
    return window.location.pathname + window.location.search + window.location.hash;
  }

  /**
   * 获取当前页面域名
   */
  public static getCurrentDomain(): string {
    return window.location.hostname;
  }

  /**
   * 获取当前页面完整URL
   */
  public static getCurrentUrl(): string {
    return window.location.href;
  }

  /**
   * 获取页面标题
   */
  public static getPageTitle(): string {
    return document.title;
  }

  /**
   * 检查是否已初始化
   */
  public static isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * 获取缓存的页面浏览时长数据数量
   */
  public static getCachedDurationCount(): number {
    return PageDurationCacheManager.getCacheSize();
  }

  /**
   * 手动触发批量上报缓存数据
   */
  public static async flushCachedDurations(): Promise<void> {
    await this.batchReportCachedDurations();
  }

  /**
   * 调试方法：获取当前状态信息
   */
  public static getDebugInfo(): object {
    return {
      isInitialized: this.isInitialized,
      currentPath: this.currentPath,
      startTime: this.startTime,
      currentTime: Date.now(),
      sessionDuration: Date.now() - this.startTime,
      hasCachedCurrentSession: this.hasCachedCurrentSession,
      isInWebView: inWebView(),
      cachedItemsCount: PageDurationCacheManager.getCacheSize(),
      cachedItems: PageDurationCacheManager.getCacheList(),
      currentUrl: typeof window !== 'undefined' ? window.location.href : 'N/A'
    };
  }

  /**
   * 重置追踪器状态（主要用于测试）
   */
  public static reset(): void {
    this.isInitialized = false;
    this.currentPath = '';
    this.startTime = Date.now();
    this.hasCachedCurrentSession = false; // 重置缓存标志
    PageDurationCacheManager.clearCache();
  }
}

/**
 * 便捷的页面追踪函数
 * 可以在任何页面组件中直接调用
 */
export async function trackPageView(): Promise<void> {
  await PageTracker.trackCurrentPage();
}

/**
 * React Hook 示例
 * 在React组件中使用
 */
export function usePageTracking() {
  // 在React组件中使用示例：
  // useEffect(() => {
  //   trackPageView();
  // }, []);

  return {
    trackPageView,
    trackPageDuration: PageTracker.trackPageDuration,
    cachePageDuration: PageTracker.cachePageDuration,
    getCurrentPath: PageTracker.getCurrentPath,
    getCurrentUrl: PageTracker.getCurrentUrl,
    getPageTitle: PageTracker.getPageTitle,
    isReady: PageTracker.isReady,
    getCachedDurationCount: PageTracker.getCachedDurationCount,
    flushCachedDurations: PageTracker.flushCachedDurations,
    getDebugInfo: PageTracker.getDebugInfo
  };
}

/**
 * Vue Composition API 示例
 */
export function usePageTrackingVue() {
  // 在Vue组件中使用示例：
  // onMounted(() => {
  //   trackPageView();
  // });

  return {
    trackPageView,
    trackPageDuration: PageTracker.trackPageDuration,
    cachePageDuration: PageTracker.cachePageDuration,
    isReady: PageTracker.isReady,
    getCachedDurationCount: PageTracker.getCachedDurationCount,
    flushCachedDurations: PageTracker.flushCachedDurations,
    getDebugInfo: PageTracker.getDebugInfo
  };
}

/**
 * 页面生命周期追踪器
 * 提供完整的页面生命周期事件追踪
 */
export class PageLifecycleTracker {
  
  /**
   * 页面加载完成时调用
   */
  static async onPageLoad(): Promise<void> {
    await PageTracker.trackCurrentPage();
  }

  /**
   * 页面即将离开时调用
   * 改为缓存页面浏览时长而不是立即上报
   * 只有在WebView环境中才会缓存
   */
  static onPageUnload(): void {
    PageTracker.cachePageDuration();
  }

  /**
   * 页面变为可见时调用
   */
  static async onPageVisible(): Promise<void> {
    await PageTracker.trackCurrentPage();
  }

  /**
   * 页面变为隐藏时调用
   * 改为缓存页面浏览时长而不是立即上报
   * 只有在WebView环境中才会缓存
   */
  static onPageHidden(): void {
    PageTracker.cachePageDuration();
  }

  /**
   * 初始化页面生命周期监听（可选）
   * 如果需要自动处理页面可见性变化，可以调用此方法
   */
  static init(): void {
    // 页面卸载时缓存停留时间
    window.addEventListener('beforeunload', this.onPageUnload);

    // 页面可见性变化时的处理（可选）
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.onPageVisible();
      } else {
        this.onPageHidden();
      }
    });
  }
}

// 导出默认的页面追踪器实例
export default PageTracker; 